import { useCallback } from 'react'
import { useMapLayers } from '@/contexts/MapLayersContext'
import L from 'leaflet'

const TILE_SERVER_HOST = 'https://tile-server.asci.co.in'

interface CFDLayerMetadata {
  gcsUrl: string
  layerType: 'dem' | 'building' | 'result'
  fileName: string
  uploadedAt: Date
}

/**
 * Hook to manage CFD simulation layers (DEM, building mask, results)
 * Dynamically adds/removes layers as files are uploaded
 */
export function useCFDLayers() {
  const { addLayer, removeLayer, updateLayer, layers, toggleLayer, setLayerOpacity } = useMapLayers()

  // Add DEM layer from GCS URL
  const addDEMLayer = useCallback(
    async (gcsUrl: string, fileName: string) => {
      const layerId = 'cfd-dem'

      // Remove existing DEM layer if present
      removeLayer(layerId)

      try {
        // Fetch metadata from TiTiler
        const infoUrl = `${TILE_SERVER_HOST}/info?url=${encodeURIComponent(gcsUrl)}`
        const infoResponse = await fetch(infoUrl)
        const info = await infoResponse.json()

        // Fetch bounds in WGS84 (lat/lng)
        const boundsUrl = `${TILE_SERVER_HOST}/bounds?url=${encodeURIComponent(gcsUrl)}`
        const boundsResponse = await fetch(boundsUrl)
        const boundsData = await boundsResponse.json()
        const bounds = boundsData.bounds

        const leafletBounds = L.latLngBounds(
          L.latLng(bounds[1], bounds[0]), // southwest
          L.latLng(bounds[3], bounds[2])  // northeast
        )

        // Create TiTiler tile URL with terrain colormap
        const tileUrl = `${TILE_SERVER_HOST}/tiles/WebMercatorQuad/{z}/{x}/{y}?url=${encodeURIComponent(gcsUrl)}&bidx=1&colormap_name=terrain`

        const metadata: CFDLayerMetadata = {
          gcsUrl,
          layerType: 'dem',
          fileName,
          uploadedAt: new Date(),
        }

        addLayer({
          id: layerId,
          name: `DEM: ${fileName}`,
          type: 'tile',
          visible: true,
          opacity: 0.7,
          zIndex: 300,
          metadata: {
            ...metadata,
            width: info.width,
            height: info.height,
            bounds,
          },
          tileConfig: {
            url: tileUrl,
            bounds: leafletBounds,
            attribution: 'DEM',
            minZoom: 5,
            maxZoom: 18,
          },
        })

        return { success: true, bounds: leafletBounds }
      } catch (error) {
        console.error('Failed to add DEM layer:', error)
        return { success: false, error }
      }
    },
    [addLayer, removeLayer]
  )

  // Add building mask layer from GCS URL
  const addBuildingLayer = useCallback(
    async (gcsUrl: string, fileName: string) => {
      const layerId = 'cfd-building'

      // Remove existing building layer if present
      removeLayer(layerId)

      try {
        // Fetch metadata from TiTiler
        const infoUrl = `${TILE_SERVER_HOST}/info?url=${encodeURIComponent(gcsUrl)}`
        const infoResponse = await fetch(infoUrl)
        const info = await infoResponse.json()

        // Fetch bounds in WGS84 (lat/lng)
        const boundsUrl = `${TILE_SERVER_HOST}/bounds?url=${encodeURIComponent(gcsUrl)}`
        const boundsResponse = await fetch(boundsUrl)
        const boundsData = await boundsResponse.json()
        const bounds = boundsData.bounds

        const leafletBounds = L.latLngBounds(
          L.latLng(bounds[1], bounds[0]), // southwest
          L.latLng(bounds[3], bounds[2])  // northeast
        )

        // Create TiTiler tile URL with binary colormap for buildings
        // Buildings are typically 0/1 or 0/255
        const tileUrl = `${TILE_SERVER_HOST}/tiles/WebMercatorQuad/{z}/{x}/{y}?url=${encodeURIComponent(gcsUrl)}&bidx=1&colormap_name=viridis`

        const metadata: CFDLayerMetadata = {
          gcsUrl,
          layerType: 'building',
          fileName,
          uploadedAt: new Date(),
        }

        addLayer({
          id: layerId,
          name: `Buildings: ${fileName}`,
          type: 'tile',
          visible: true,
          opacity: 0.6,
          zIndex: 310,
          metadata: {
            ...metadata,
            width: info.width,
            height: info.height,
            bounds,
          },
          tileConfig: {
            url: tileUrl,
            bounds: leafletBounds,
            attribution: 'Building Mask',
            minZoom: 5,
            maxZoom: 18,
          },
        })

        return { success: true, bounds: leafletBounds }
      } catch (error) {
        console.error('Failed to add building layer:', error)
        return { success: false, error }
      }
    },
    [addLayer, removeLayer]
  )

  // Add simulation result layer from GCS URL or path
  const addResultLayer = useCallback(
    async (gcsUrl: string, fileName: string, colormap?: string) => {
      const layerId = 'cfd-result'

      // Remove existing result layer if present
      removeLayer(layerId)

      try {
        // Fetch metadata from TiTiler
        const infoUrl = `${TILE_SERVER_HOST}/info?url=${encodeURIComponent(gcsUrl)}`
        const infoResponse = await fetch(infoUrl)
        const info = await infoResponse.json()

        // Fetch bounds in WGS84 (lat/lng)
        const boundsUrl = `${TILE_SERVER_HOST}/bounds?url=${encodeURIComponent(gcsUrl)}`
        const boundsResponse = await fetch(boundsUrl)
        const boundsData = await boundsResponse.json()
        const bounds = boundsData.bounds

        // Fetch statistics to get min/max values for rescaling
        const statsUrl = `${TILE_SERVER_HOST}/statistics?url=${encodeURIComponent(gcsUrl)}`
        const statsResponse = await fetch(statsUrl)
        const stats = await statsResponse.json()
        const bandStats = stats.b1 || stats['1'] || {}
        const minVal = bandStats.min ?? 0
        const maxVal = bandStats.max ?? 1

        const leafletBounds = L.latLngBounds(
          L.latLng(bounds[1], bounds[0]), // southwest
          L.latLng(bounds[3], bounds[2])  // northeast
        )

        // Create TiTiler tile URL with colormap and rescale based on actual data range
        // Default to blues_r for water depth (darker = deeper)
        const colormapName = colormap || 'blues_r'
        const rescale = `${minVal},${maxVal}`
        const tileUrl = `${TILE_SERVER_HOST}/tiles/WebMercatorQuad/{z}/{x}/{y}?url=${encodeURIComponent(gcsUrl)}&bidx=1&colormap_name=${colormapName}&rescale=${rescale}`

        const metadata: CFDLayerMetadata = {
          gcsUrl,
          layerType: 'result',
          fileName,
          uploadedAt: new Date(),
        }

        addLayer({
          id: layerId,
          name: `Result: ${fileName}`,
          type: 'tile',
          visible: true,
          opacity: 0.8,
          zIndex: 320,
          metadata: {
            ...metadata,
            width: info.width,
            height: info.height,
            bounds,
          },
          tileConfig: {
            url: tileUrl,
            bounds: leafletBounds,
            attribution: 'Simulation Result',
            minZoom: 5,
            maxZoom: 18,
          },
        })

        return { success: true, bounds: leafletBounds }
      } catch (error) {
        console.error('Failed to add result layer:', error)
        return { success: false, error }
      }
    },
    [addLayer, removeLayer]
  )

  // Add filtered ponds layer with greens colormap (distinct from peak depth blues)
  const addPondsLayer = useCallback(
    async (gcsUrl: string, fileName: string) => {
      const layerId = 'cfd-ponds'

      // Remove existing ponds layer if present
      removeLayer(layerId)

      try {
        // Fetch metadata from TiTiler
        const infoUrl = `${TILE_SERVER_HOST}/info?url=${encodeURIComponent(gcsUrl)}`
        const infoResponse = await fetch(infoUrl)
        const info = await infoResponse.json()

        // Fetch bounds in WGS84 (lat/lng)
        const boundsUrl = `${TILE_SERVER_HOST}/bounds?url=${encodeURIComponent(gcsUrl)}`
        const boundsResponse = await fetch(boundsUrl)
        const boundsData = await boundsResponse.json()
        const bounds = boundsData.bounds

        // Fetch statistics to get min/max values for rescaling
        const statsUrl = `${TILE_SERVER_HOST}/statistics?url=${encodeURIComponent(gcsUrl)}`
        const statsResponse = await fetch(statsUrl)
        const stats = await statsResponse.json()
        const bandStats = stats.b1 || stats['1'] || {}
        const minVal = bandStats.min ?? 0
        const maxVal = bandStats.max ?? 1

        const leafletBounds = L.latLngBounds(
          L.latLng(bounds[1], bounds[0]), // southwest
          L.latLng(bounds[3], bounds[2])  // northeast
        )

        // Use greens colormap for ponds to distinguish from peak depth (blues)
        // Rescale based on actual data range for better visibility
        const colormapName = 'greens'
        const rescale = `${minVal},${maxVal}`
        const tileUrl = `${TILE_SERVER_HOST}/tiles/WebMercatorQuad/{z}/{x}/{y}?url=${encodeURIComponent(gcsUrl)}&bidx=1&colormap_name=${colormapName}&rescale=${rescale}`

        const metadata: CFDLayerMetadata = {
          gcsUrl,
          layerType: 'result',
          fileName,
          uploadedAt: new Date(),
        }

        addLayer({
          id: layerId,
          name: `Ponds: ${fileName}`,
          type: 'tile',
          visible: true,
          opacity: 0.8,
          zIndex: 325,
          metadata: {
            ...metadata,
            width: info.width,
            height: info.height,
            bounds,
          },
          tileConfig: {
            url: tileUrl,
            bounds: leafletBounds,
            attribution: 'Filtered Ponds',
            minZoom: 5,
            maxZoom: 18,
          },
        })

        return { success: true, bounds: leafletBounds }
      } catch (error) {
        console.error('Failed to add ponds layer:', error)
        return { success: false, error }
      }
    },
    [addLayer, removeLayer]
  )

  // Remove all CFD layers
  const clearAllCFDLayers = useCallback(() => {
    removeLayer('cfd-dem')
    removeLayer('cfd-building')
    removeLayer('cfd-result')
  }, [removeLayer])

  // Get CFD layers from the context
  const cfdLayers = layers.filter((l) => l.id.startsWith('cfd-'))

  return {
    layers: cfdLayers,
    addDEMLayer,
    addBuildingLayer,
    addResultLayer,
    addPondsLayer,
    clearAllCFDLayers,
    toggleLayer,
    setOpacity: setLayerOpacity,
    updateLayer,
  }
}
